import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { NoxoButton } from '@noxo/ui/button';
import { NoxoCardOverline } from '@noxo/ui/card';
import { NoxoIcon } from '@noxo/ui/icon';
import { NoxoListRow, NoxoListRowCaption, NoxoListRowTitle } from '@noxo/ui/list-row';
import { NoxoSwitch } from '@noxo/ui/switch';

import { DemoOutbox } from './demo-outbox';

const CHANNELS = [
  {
    channel: 'email',
    label: 'E-mail',
    icon: 'envelope',
    empty: 'No e-mails yet. Sign-in, registration and confirmation codes land here.',
  },
  {
    channel: 'sms',
    label: 'SMS',
    icon: 'comment-lines',
    empty: 'No SMS yet. Phone verification and SMS step-up codes land here.',
  },
] as const;

@Component({
  selector: 'noxo-demo-inbox',
  imports: [NoxoButton, NoxoCardOverline, NoxoIcon, NoxoListRow, NoxoListRowCaption, NoxoListRowTitle, NoxoSwitch],
  template: `
    <label class="toggle">
      <span class="text">
        <span>Send e-mails for real</span>
        <small>Off: codes land here. On: e-mails go to the real address, SMS stay here.</small>
      </span>
      <noxo-switch>
        <input
          #realEmail
          type="checkbox"
          role="switch"
          [checked]="outbox.realEmail()"
          (change)="outbox.setRealEmail(realEmail.checked)" />
      </noxo-switch>
    </label>
    @for (channel of channels(); track channel.label) {
      <section>
        <noxo-card-overline>{{ channel.label }}</noxo-card-overline>
        @for (message of channel.messages; track $index) {
          <div
            noxo-list-row
            tone="brand">
            <noxo-icon [icon]="channel.icon" />
            <noxo-list-row-title>{{ message.code ?? message.body }}</noxo-list-row-title>
            <noxo-list-row-caption>{{ message.to }} · {{ message.purpose }} · {{ message.time }}</noxo-list-row-caption>
            @if (message.code; as code) {
              <button
                type="button"
                noxo-button="tertiary"
                size="xs"
                noxoListRowTrailing
                (click)="copy(code)">
                Copy
              </button>
            }
            @if (message.link; as link) {
              <a
                noxo-button="tertiary"
                size="xs"
                noxoListRowTrailing
                [href]="link.href"
                >{{ link.label }}</a
              >
            }
          </div>
        } @empty {
          <p class="hint">{{ channel.empty }}</p>
        }
      </section>
    }
    @if (messages().length > 0) {
      <button
        type="button"
        noxo-button="tertiary"
        size="xs"
        (click)="outbox.clear()">
        Clear inbox
      </button>
    }
  `,
  styleUrl: './demo-overlay-section.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoInbox {
  private readonly clipboard = inject(DOCUMENT).defaultView?.navigator.clipboard;
  protected readonly outbox = inject(DemoOutbox);
  protected readonly messages = this.outbox.messages;
  protected readonly channels = computed(() =>
    CHANNELS.map(({ channel, ...rest }) =>
      Object.assign(rest, {
        messages: this.outbox
          .messages()
          .filter((message) => message.channel === channel)
          .map((message) => ({
            ...message,
            time: new Date(message.receivedAt).toLocaleTimeString(undefined, { timeStyle: 'short' }),
          })),
      })
    )
  );

  protected copy(code: string): void {
    void this.clipboard?.writeText(code);
  }
}
